/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { useSelector } from "react-redux";
import { Drawer, Typography, Grid, IconButton, Stack, Box, CircularProgress, Tabs, Tab } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { IFullDeviceDetails } from "../../../store/api/deviceConfig/types";
import { isOpenCellDrawerSelector, selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { setOpenCellDrawer } from "../../../store/api/devices/reducers";
import { useStoreDispatch } from "../../../store/hooks";
import { useEmbeddedDrawer } from "./hooks";
import CheckboxLabel from "../../../components/Shared/CheckBox/CheckboxLabel";
import { handleBooleanStringValues } from "../../../utils/helpers";
import StatusIcon from "../../../components/Shared/Status/StatusIcon";
import CellKpisChartTab from "./CellKpisChartTab";

interface IDeviceDrawerProps {
  cellDetails?: any | IFullDeviceDetails;
  closedButtonClicked?: (arg: boolean) => void;
  callbackFun?: () => void;
}

const EmbeddedDrawer: React.FC<IDeviceDrawerProps> = ({ cellDetails }) => {
  const dispatch = useStoreDispatch();
  const isOpenCellDrawer = useSelector(isOpenCellDrawerSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const { cellData, isLoading, zones } = useEmbeddedDrawer(cellDetails?.cell_name || "");
  const [activeTab, setActiveTab] = React.useState(0);
  const supportsSaCharts = ["RCP", "ME1200"].includes((selectedDevice?.nodeType || "").toUpperCase());

  React.useEffect(() => {
    if (!supportsSaCharts && activeTab === 1) {
      setActiveTab(0);
    }
  }, [supportsSaCharts, activeTab]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);
  const handleClose = () => {
    dispatch(setOpenCellDrawer(false));
  };

  const lteBandWidth = cellData?.lteCellConfig?.lte?.lteRan?.lteRanRf?.dlBandwidth || "";
  const nrBAndWidth = cellData?.nrcellConfig?.ng?.ngran?.ngranRf?.dlBandwidth || "";

  const rowHeader1st = [
    { key: "CELL ID", value: cellData?.index || "" },
    { key: "STATUS", value: <StatusIcon status={cellData?.operState || ""} /> },
    { key: "ENABLED", value: <CheckboxLabel disabled label="Enable" name="enable" checked={handleBooleanStringValues(cellData?.enable) || false} /> }
  ];

  const rowHeader2nd = [
    { key: "ZONES", value: zones || 0 },
    { key: "RADIONODE", value: cellData?.radioNode || "" },
    { key: "DL BANDWIDTH", value: (selectedDevice?.nodeType === "SN" ? lteBandWidth : nrBAndWidth) || "" },
  ];

  return (
    <>
      <Drawer
        PaperProps={{
          sx: {
            width: "75%",
            maxWidth: "90vw",
            height: "100%",
            top: 0,
            backgroundColor: "var(--background)",
            borderLeft: "1px solid var(--border)",
            borderRadius: "10px 0 0 10px",
            boxShadow: "-4px 0 24px 0 rgba(0,0,0,0.08)",
            overflow: "hidden",
          }
        }}
        ModalProps={{ disableEnforceFocus: true }}
        anchor={"right"}
        open={isOpenCellDrawer}
        onClose={handleClose}
      >
        <div className="flex h-full min-h-0 flex-col bg-background">
          <div className="shrink-0 px-6 pb-4 pt-4">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography component="h3" variant="h4" className="truncate pr-4">
                {cellDetails?.cell_name || "Unknown"}
              </Typography>
              <IconButton onClick={handleClose} size="small" aria-label="Close">
                <CloseIcon fontSize="large" sx={{ color: "var(--muted-foreground)" }} />
              </IconButton>
            </Stack>
          </div>

          <div className="flex-1 min-h-0 overflow-auto px-6 pb-6">
            <Grid container rowSpacing={3}>
              <Grid item xs={12}>
                <div className="rounded-xl border bg-card text-card-foreground shadow">
                  <div className="px-4 py-3">
                    <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                      {[...rowHeader1st, ...rowHeader2nd].map((item) => (
                        <div key={item.key} className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            {item.key}
                          </span>
                          <span className="flex items-center text-sm font-semibold text-card-foreground">
                            {isLoading ? <CircularProgress size={16} color="primary" /> : item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ width: "100%", mt: 1 }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="cell charts tabs">
                      <Tab label="Charts" id="cell-tab-0" aria-controls="cell-tabpanel-0" />
                        {supportsSaCharts && (
                        <Tab label="Charts - SA" id="cell-tab-1" aria-controls="cell-tabpanel-1" />
                      )}
                    </Tabs>
                  </Box>

                  <Box
                    role="tabpanel"
                    hidden={activeTab !== 0}
                    id="cell-tabpanel-0"
                    aria-labelledby="cell-tab-0"
                  >
                    {activeTab === 0 && (
                      <CellKpisChartTab
                        siteName={selectedDevice.serialNumber}
                        cellName={cellDetails?.cell_name}
                        nodeType={selectedDevice.nodeType}
                      />
                    )}
                  </Box>

                  {supportsSaCharts && activeTab === 1 && (
                    <Box
                      role="tabpanel"
                      id="cell-tabpanel-1"
                      aria-labelledby="cell-tab-1"
                    >
                      <CellKpisChartTab
                        siteName={selectedDevice.serialNumber}
                        cellName={cellDetails?.cell_name}
                        nodeType={selectedDevice.nodeType}
                        technology="SA"
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default EmbeddedDrawer;
